import React from 'react';
import {
    RiFacebookLine as Facebook,
    RiInstagramLine as Instagram,
    RiTiktokFill as Tiktok,
    RiWhatsappLine as Whatsapp,
    RiLinkedinLine as Linkedin,
    RiTelegramLine as Telegram,
} from '@remixicon/react';

export type SocialNetwork =
    | 'whatsapp'
    | 'facebook'
    | 'instagram'
    | 'tiktok'
    | 'linkedin'
    | 'telegram'
    | 'spotify'
    | 'pinterest'
    | 'dribbble';

export interface SocialItem {
    id: string;
    social: SocialNetwork;
    name: string;
    url: string;
    icon?: React.ReactNode;
}

export interface SocialIconsProps {
    items?: SocialItem[];
    className?: string;
    style?: React.CSSProperties;
    iconSize?: number;
    buttonSize?: number;
    align?: 'center' | 'left' | 'right';
    variant?: 'light' | 'dark' | 'glass';
    gap?: number;
}

const DEFAULT_ITEMS: SocialItem[] = [
    {
        id: 'facebook',
        social: 'facebook',
        name: 'Facebook',
        url: 'https://www.facebook.com/CoMarcaCoworkconProposito/',
        icon: <Facebook size={26} />,
    },
    {
        id: 'instagram',
        social: 'instagram',
        name: 'Instagram',
        url: 'https://www.instagram.com/comarcacowork/',
        icon: <Instagram size={26} />,
    },
    {
        id: 'tiktok',
        social: 'tiktok',
        name: 'TikTok',
        url: 'https://www.tiktok.com/@co_marca_cowork',
        icon: <Tiktok size={26} />,
    },
    {
        id: 'whatsapp',
        social: 'whatsapp',
        name: 'WhatsApp',
        url: 'https://api.whatsapp.com/send?phone=59176204601&text=Hola%20desde%20la%20web',
        icon: <Whatsapp size={26} />,
    },
];

export const SocialIcons: React.FC<SocialIconsProps> = ({
    items = DEFAULT_ITEMS,
    className = '',
    style = {},
    iconSize = 26,
    buttonSize = 52,
    align = 'center',
    variant = 'light',
    gap = 12,
}) => {
    const justifyMap = {
        center: 'center',
        left: 'flex-start',
        right: 'flex-end',
    };

    return (
        <div
            className={`social-icons-wrapper ${className}`}
            style={{
                display: 'flex',
                justifyContent: justifyMap[align],
                width: '100%',
                ...style,
            }}
        >
            <style>{`
                /* Contenedor principal con variables CSS por instancia */
                ul.example-2 {
                    list-style: none;
                    display: flex;
                    align-items: center;
                    padding: 0;
                    margin: 0;
                    flex-wrap: wrap;
                }

                .example-2 .icon-content {
                    margin: 0;
                    position: relative;
                    display: flex;
                    justify-content: center;
                    align-items: center;
                }

                /* ═══ Botón Base ═══ */
                .example-2 .icon-content a {
                    position: relative;
                    overflow: hidden;
                    display: flex;
                    justify-content: center;
                    align-items: center;
                    width: var(--btn-size, 50px);
                    height: var(--btn-size, 50px);
                    border-radius: var(--btn-radius, 16px);
                    text-decoration: none;
                    cursor: pointer;
                    transition: all 0.35s cubic-bezier(0.2, 0.8, 0.2, 1);
                }

                .example-2 .icon-content a svg {
                    position: relative;
                    z-index: 2;
                    width: var(--icon-size, 24px);
                    height: var(--icon-size, 24px);
                    fill: currentColor;
                    transition: transform 0.35s cubic-bezier(0.34, 1.56, 0.64, 1), color 0.25s ease;
                }

                /* ═══ Variante Light (Fondo blanco en tarjetas claras) ═══ */
                .example-2.variant-light .icon-content a {
                    background-color: #ffffff;
                    color: #334155;
                    border: 1.5px solid #e2e8f0;
                    box-shadow: 0 3px 12px rgba(0, 0, 0, 0.05), 0 1px 2px rgba(0, 0, 0, 0.02);
                }

                .example-2.variant-light .icon-content a[data-social="facebook"] {
                    color: #1877f2;
                }
                .example-2.variant-light .icon-content a[data-social="instagram"] {
                    color: #e1306c;
                }
                .example-2.variant-light .icon-content a[data-social="tiktok"] {
                    color: #0f172a;
                }
                .example-2.variant-light .icon-content a[data-social="whatsapp"] {
                    color: #16a34a;
                }

                /* ═══ Variante Glass (Fondo oscuro o degradado en footer) ═══ */
                .example-2.variant-glass .icon-content a {
                    background-color: rgba(255, 255, 255, 0.08);
                    color: rgba(255, 255, 255, 0.9);
                    border: 1.5px solid rgba(255, 255, 255, 0.16);
                    box-shadow: 0 4px 14px rgba(0, 0, 0, 0.18);
                    backdrop-filter: blur(8px);
                }

                /* ═══ Variante Dark ═══ */
                .example-2.variant-dark .icon-content a {
                    background-color: rgba(255, 255, 255, 0.14);
                    color: #ffffff;
                    border: 1.5px solid rgba(255, 255, 255, 0.22);
                }

                /* Hover general en el botón */
                .example-2 .icon-content a:hover {
                    color: #ffffff !important;
                    transform: translateY(-5px) scale(1.06);
                    border-color: transparent;
                }

                .example-2 .icon-content a:hover svg {
                    transform: scale(1.15) rotate(-3deg);
                    color: #ffffff !important;
                    fill: #ffffff !important;
                }

                .example-2 .icon-content a:active {
                    transform: translateY(-2px) scale(0.97);
                }

                /* Fondo deslizable desde abajo (.filled) */
                .example-2 .icon-content a .filled {
                    position: absolute;
                    top: auto;
                    bottom: 0;
                    left: 0;
                    width: 100%;
                    height: 0;
                    border-radius: 0 0 var(--btn-radius, 16px) var(--btn-radius, 16px);
                    transition: height 0.38s cubic-bezier(0.4, 0, 0.2, 1), border-radius 0.25s ease;
                    z-index: 1;
                }

                .example-2 .icon-content a:hover .filled {
                    height: 100%;
                    border-radius: var(--btn-radius, 16px);
                }

                /* ═══ Tooltip flotante ═══ */
                .example-2 .icon-content .tooltip {
                    position: absolute;
                    top: -26px;
                    left: 50%;
                    transform: translateX(-50%) translateY(8px) scale(0.85);
                    color: #ffffff;
                    padding: 5px 12px;
                    border-radius: 999px;
                    opacity: 0;
                    visibility: hidden;
                    font-size: 11px;
                    font-weight: 800;
                    letter-spacing: 0.05em;
                    text-transform: uppercase;
                    white-space: nowrap;
                    pointer-events: none;
                    z-index: 50;
                    box-shadow: 0 10px 22px -3px rgba(0, 0, 0, 0.32);
                    transition: all 0.32s cubic-bezier(0.34, 1.56, 0.64, 1);
                }

                /* Flecha inferior del tooltip */
                .example-2 .icon-content .tooltip::after {
                    content: '';
                    position: absolute;
                    bottom: -4px;
                    left: 50%;
                    transform: translateX(-50%);
                    border-width: 4px 4px 0 4px;
                    border-style: solid;
                    border-color: inherit transparent transparent transparent;
                }

                /* Hover activa elevación del tooltip */
                .example-2 .icon-content:hover .tooltip {
                    opacity: 1;
                    visibility: visible;
                    top: -46px;
                    transform: translateX(-50%) translateY(0) scale(1);
                }

                /* ═══ Colores por Red Social ═══ */

                /* WhatsApp */
                .example-2 .icon-content a[data-social="whatsapp"] .filled,
                .example-2 .icon-content a[data-social="whatsapp"] ~ .tooltip {
                    background-color: #25d366;
                    border-color: #25d366;
                }
                .example-2 .icon-content a[data-social="whatsapp"]:hover {
                    box-shadow: 0 12px 26px -4px rgba(37, 211, 102, 0.52), 0 0 18px rgba(37, 211, 102, 0.3);
                }

                /* Facebook */
                .example-2 .icon-content a[data-social="facebook"] .filled,
                .example-2 .icon-content a[data-social="facebook"] ~ .tooltip {
                    background-color: #1877f2;
                    border-color: #1877f2;
                }
                .example-2 .icon-content a[data-social="facebook"]:hover {
                    box-shadow: 0 12px 26px -4px rgba(24, 119, 242, 0.52), 0 0 18px rgba(24, 119, 242, 0.3);
                }

                /* Instagram */
                .example-2 .icon-content a[data-social="instagram"] .filled {
                    background: linear-gradient(45deg, #f09433 0%, #e6683c 25%, #dc2743 50%, #cc2366 75%, #bc1888 100%);
                }
                .example-2 .icon-content a[data-social="instagram"] ~ .tooltip {
                    background-color: #e1306c;
                    border-color: #e1306c;
                }
                .example-2 .icon-content a[data-social="instagram"]:hover {
                    box-shadow: 0 12px 26px -4px rgba(225, 48, 108, 0.52), 0 0 18px rgba(225, 48, 108, 0.3);
                }

                /* TikTok */
                .example-2 .icon-content a[data-social="tiktok"] .filled,
                .example-2 .icon-content a[data-social="tiktok"] ~ .tooltip {
                    background-color: #010101;
                    border-color: #010101;
                }
                .example-2 .icon-content a[data-social="tiktok"]:hover {
                    box-shadow: 0 12px 26px -4px rgba(0, 0, 0, 0.65), 0 0 18px rgba(254, 44, 85, 0.35);
                }

                /* LinkedIn */
                .example-2 .icon-content a[data-social="linkedin"] .filled,
                .example-2 .icon-content a[data-social="linkedin"] ~ .tooltip {
                    background-color: #0077b5;
                    border-color: #0077b5;
                }
                .example-2 .icon-content a[data-social="linkedin"]:hover {
                    box-shadow: 0 12px 26px -4px rgba(0, 119, 181, 0.52), 0 0 18px rgba(0, 119, 181, 0.3);
                }

                /* Telegram */
                .example-2 .icon-content a[data-social="telegram"] .filled,
                .example-2 .icon-content a[data-social="telegram"] ~ .tooltip {
                    background-color: #0088cc;
                    border-color: #0088cc;
                }
                .example-2 .icon-content a[data-social="telegram"]:hover {
                    box-shadow: 0 12px 26px -4px rgba(0, 136, 204, 0.52), 0 0 18px rgba(0, 136, 204, 0.3);
                }

                /* Spotify */
                .example-2 .icon-content a[data-social="spotify"] .filled,
                .example-2 .icon-content a[data-social="spotify"] ~ .tooltip {
                    background-color: #1db954;
                    border-color: #1db954;
                }
                .example-2 .icon-content a[data-social="spotify"]:hover {
                    box-shadow: 0 12px 26px -4px rgba(29, 185, 84, 0.52), 0 0 18px rgba(29, 185, 84, 0.3);
                }

                /* Pinterest */
                .example-2 .icon-content a[data-social="pinterest"] .filled,
                .example-2 .icon-content a[data-social="pinterest"] ~ .tooltip {
                    background-color: #bd081c;
                    border-color: #bd081c;
                }
                .example-2 .icon-content a[data-social="pinterest"]:hover {
                    box-shadow: 0 12px 26px -4px rgba(189, 8, 28, 0.52), 0 0 18px rgba(189, 8, 28, 0.3);
                }

                /* Dribbble */
                .example-2 .icon-content a[data-social="dribbble"] .filled,
                .example-2 .icon-content a[data-social="dribbble"] ~ .tooltip {
                    background-color: #ea4c89;
                    border-color: #ea4c89;
                }
                .example-2 .icon-content a[data-social="dribbble"]:hover {
                    box-shadow: 0 12px 26px -4px rgba(234, 76, 137, 0.52), 0 0 18px rgba(234, 76, 137, 0.3);
                }

                @media (max-width: 480px) {
                    ul.example-2 {
                        gap: 8px !important;
                    }
                    .example-2 .icon-content a {
                        width: clamp(40px, 11vw, 46px) !important;
                        height: clamp(40px, 11vw, 46px) !important;
                        border-radius: 14px !important;
                    }
                    .example-2 .icon-content a svg {
                        width: clamp(19px, 5.5vw, 22px) !important;
                        height: clamp(19px, 5.5vw, 22px) !important;
                    }
                    .example-2 .icon-content .tooltip {
                        font-size: 10px !important;
                        padding: 4px 10px !important;
                    }
                }
            `}</style>

            <ul
                className={`example-2 variant-${variant}`}
                style={
                    {
                        justifyContent: justifyMap[align],
                        gap: `${gap}px`,
                        ['--btn-size' as any]: `${buttonSize}px`,
                        ['--btn-radius' as any]: `${Math.round(buttonSize * 0.35)}px`,
                        ['--icon-size' as any]: `${iconSize}px`,
                    } as React.CSSProperties
                }
            >
                {items.map((item) => (
                    <li key={item.id} className="icon-content">
                        <a
                            href={item.url}
                            data-social={item.social}
                            target="_blank"
                            rel="noopener noreferrer"
                            aria-label={item.name}
                        >
                            <div className="filled" />
                            {item.icon}
                        </a>
                        <div className="tooltip">{item.name}</div>
                    </li>
                ))}
            </ul>
        </div>
    );
};

export default SocialIcons;
